import { useCallback, useEffect, useMemo, useState } from 'react'
import { MotionConfig } from 'motion/react'
import { CertificatesSection } from './components/CertificatesSection'
import { EducationSection } from './components/EducationSection'
import { ExperienceList } from './components/ExperienceList'
import { Footer } from './components/Footer'
import { Header } from './components/Header'
import { Hero } from './components/Hero'
import { ImpactRail } from './components/ImpactRail'
import { LanguagesSection } from './components/LanguagesSection'
import { useLocale } from './context/LocaleContext'
import { useActiveSection } from './hooks/useActiveSection'

const legacyRouteTargets: Record<string, string> = {
  '/experience/korpu': 'experience-korpu',
  '/experience/abona': 'experience-abona',
  '/experience/kivi': 'experience-kivi',
  '/experience/ozon': 'experience-ozon',
  '/experience/onetrak': 'experience-onetrak',
  '/Education': 'education',
  '/Certificates': 'learning',
}

export default function App() {
  const { content } = useLocale()
  const sectionIds = useMemo(() => [
    ...content.experiences.map((item) => `experience-${item.id}`),
    ...content.education.map((item) => `education-${item.id}`),
  ], [content.education, content.experiences])
  const legacyTarget = legacyRouteTargets[window.location.pathname.replace(/\/$/, '')]
  const initialTarget = legacyTarget ?? window.location.hash.replace('#', '')
  const [activeId, setActiveId] = useState(sectionIds.includes(initialTarget) ? initialTarget : '')
  const onActive = useCallback((id: string) => setActiveId(id), [])
  useActiveSection(sectionIds, onActive)

  useEffect(() => {
    if (!legacyTarget) return
    window.history.replaceState(null, '', `/#${legacyTarget}`)
    document.getElementById(legacyTarget)?.scrollIntoView({ behavior: 'auto', block: 'start' })
  }, [legacyTarget])

  const navigate = useCallback((id: string) => {
    const target = document.getElementById(id)
    if (!target) return
    if (id.startsWith('experience-') || id.startsWith('education-')) setActiveId(id)
    window.history.pushState(null, '', `#${id}`)
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    if (id === 'top') {
      const previousScrollBehavior = document.documentElement.style.scrollBehavior
      document.documentElement.style.scrollBehavior = 'auto'
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      document.documentElement.style.scrollBehavior = previousScrollBehavior
    } else target.scrollIntoView({ behavior, block: 'start' })
  }, [])

  return (
    <MotionConfig reducedMotion="user">
      <a className="skip-link" href="#experience">{content.ui.skipToExperience}</a>
      <Header onNavigate={navigate} />
      <main>
        <Hero activeId={activeId} onNavigate={navigate} />
        <ImpactRail />
        <ExperienceList />
        <EducationSection />
        <CertificatesSection />
        <LanguagesSection />
      </main>
      <Footer onNavigate={navigate} />
    </MotionConfig>
  )
}
